// src/components/ResendRegistrationLinkBanner.jsx
import React, { useState } from "react";
import axios from "axios";

/**
 * Props:
 * - email (string, required): the user's email to resend the link to
 * - compact (bool, optional): render a tighter layout (defaults to false)
 * - onSent (fn, optional): called after successful resend
 * - onError (fn, optional): called with error message on failure
 * - className (string, optional): extra class for the wrapper
 */
const ResendRegistrationLinkBanner = ({ email, compact = false, onSent, onError, className = "" }) => {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const resend = async () => {
    if (!email) return;
    setBusy(true);
    setMsg("");
    setErr("");
    try {
      const { data } = await axios.post(
        "https://ghrianghraffy.com/auth/resend-complete-registration",
        { email }, // <-- matches embed=True (expects {"email": "..."} as JSON)
        { headers: { "Content-Type": "application/json" } }
      );
      const text = data?.message || "A new completion link has been sent to your email.";
      setMsg(text);
      onSent?.(text);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed to resend the setup link.";
      setErr(detail);
      onError?.(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`gg-resend-banner ${className}`}
      style={{
        border: "1px solid #bfdbfe",
        background: "#eff6ff",
        color: "#1e3a8a",
        borderRadius: 8,
        padding: compact ? "8px 10px" : "12px 14px",
      }}
    >
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <span>
          Can’t use the link? We can send a fresh setup link to <strong>{email || "your email"}</strong>.
        </span>
        <button
          type="button"
          onClick={resend}
          disabled={busy || !email}
          style={{
            padding: compact ? "6px 10px" : "8px 12px",
            borderRadius: 6,
            border: "1px solid #93c5fd",
            background: "#ffffff",
            cursor: busy || !email ? "not-allowed" : "pointer",
          }}
        >
          {busy ? "Sending…" : "Resend setup link"}
        </button>
      </div>

      {msg && (
        <div
          style={{
            marginTop: 8,
            padding: "6px 8px",
            borderRadius: 6,
            background: "#ecfdf5",
            color: "#065f46",
            border: "1px solid #a7f3d0",
          }}
          role="status"
          aria-live="polite"
        >
          {msg}
        </div>
      )}
      {err && (
        <div
          style={{
            marginTop: 8,
            padding: "6px 8px",
            borderRadius: 6,
            background: "#fef2f2",
            color: "#991b1b",
            border: "1px solid #fecaca",
          }}
          role="alert"
        >
          {err}
        </div>
      )}
    </div>
  );
};

export default ResendRegistrationLinkBanner;
